import { Speed } from "@mui/icons-material";
import { BasicFormikForm } from "../form/BasicFormikForm";
import BasicModal from "../helpers/BasicModal";
import { useEffect, useState } from "react";
import { useFormik } from "formik";
import { array, boolean, number, object, setLocale, string } from "yup";
import { en } from "yup-locales";
import { _ACTIONS, _HTTP, _MESSAGES, _SWAL_ICONS } from "../../utils/constants";
import { _ROUTES } from "../../utils/_routes_constants";
import { DataService } from "../../services/DataService";
import { buildOptions } from "../../utils/formHelper";
import { MentionInput } from "../form/MentionInput";
import { createToast } from "../../utils/toast";

export const IndicatorModal = ({
  showModal = false,
  setShowModal = () => {},
  editData = null,
  onDataMutation = () => {},
}) => {
  const [validationSchema, setValidationSchema] = useState(null);
  const [customStructure, setCustomStructure] = useState(null);
  const dataService = new DataService();
  const [options, setOptions] = useState({
    accountParameters: [],
  });

  setLocale(en);

  const formik = useFormik({
    initialValues: {
      name: "",
      displayOrder: 0,
      display: true,
      blankLineAbove: false,
      useFormula: false,
      formula: "",
      accountParameters: [],
      colorText: "#000000",
      colorBackground: "#ffffff",
    },
    onSubmit: (values) => {
      const indicator = {
        ...values,
      };

      if (
        indicator.accountParameters &&
        indicator.accountParameters.length > 0
      ) {
        indicator.accountParameters = indicator.accountParameters.map(
          (el) => el.value,
        );
      }

      // If editing an existing indicator, make a direct API request
      if (editData && editData.id) {
        const method = _ACTIONS.PATCH;
        const url = `${_ROUTES.INDICATORS}/${editData.id}`;

        try {
          dataService.doRequest(method, url, indicator, {}, async (res) => {
            if (res.status === _HTTP.ok) {
              const responseData = await res.json();
              onDataMutation(editData, responseData);
              setShowModal(false);
              createToast(
                _SWAL_ICONS.success,
                "Indicator updated successfully",
              );
            }
          });
        } catch (error) {
          createToast(_SWAL_ICONS.error, _MESSAGES.error);
        }
      } else {
        // For new indicators, use the callback method
        onDataMutation(editData, indicator);
        setShowModal(false);
      }
    },
    validationSchema: validationSchema,
  });

  // Generate the validation schema based on current values
  const updateValidationSchema = () => {
    const validationSchemaObject = {
      name: string().required().label("Name"),
      displayOrder: number().label("Order"),
      display: boolean().label("Display"),
      blankLineAbove: boolean().label("Blank Line Above"),
      useFormula: boolean().label("Use Formula"),
      formula: string().label("Formula"),
      accountParameters: array().label("Account Parameters"),
      colorText: string().label("Text Color"),
      colorBackground: string().label("Background Color"),
    };

    if (formik.values.useFormula) {
      delete validationSchemaObject.accountParameters;
    } else {
      delete validationSchemaObject.formula;
    }

    setValidationSchema(object(validationSchemaObject));
  };

  const getCustomStructure = () => {
    const structure = {
      0: ["name"],
      0.5: ["displayOrder"],
      1: ["display", "blankLineAbove"],
      2: ["useFormula"],
    };

    // Add the appropriate field based on useFormula value
    if (formik.values.useFormula) {
      structure[2.5] = ["formula"];
    } else {
      structure[2.5] = ["accountParameters"];
    }

    structure[3] = ["colorText", "colorBackground"];

    return structure;
  };

  // Update custom structure when needed
  const updateCustomStructure = () => {
    const structure = getCustomStructure();
    setCustomStructure(structure);
  };

  formik.special = {
    placeholder: {
      accountParameters: "Select account parameters",
    },
    helperText: {
      useFormula: "Enable formula calculation",
      formula: "Enter formula expression",
      accountParameters: "The sum will be realized",
      displayOrder: "Order of display in lists",
    },
    customStructure: customStructure || getCustomStructure(),
    customComponents: {
      formula: (
        <MentionInput
          value={formik.values.formula}
          onChange={(value) => formik.setFieldValue("formula", value)}
          label="Formula"
          helperText={`
            Start by typing @ to see the available accounts.
            You can also use the following operators: + - * / ( ).
            Ex: @Account1 * @Account2
          `}
          endpoint={_ROUTES.ACCOUNTS}
          displayField="name"
          extraFields={["accountNumber"]}
          entityType="account"
          renderSuggestion={(suggestion, search, highlightedDisplay) => (
            <div>
              <strong>{suggestion.display}</strong>
              {suggestion.accountNumber && ` (${suggestion.accountNumber})`}
            </div>
          )}
        />
      ),
    },
    autocomplete: {
      accountParameters: {
        optionLabel: "name",
        optionValue: "id",
        multiple: true,
      },
    },
    type: {
      colorText: "color",
      colorBackground: "color",
    },
  };

  useEffect(() => {
    if (editData) {
      const accountParams = [];

      if (editData.accountParameters && editData.accountParameters.length > 0) {
        editData.accountParameters.forEach((el) => {
          accountParams.push({
            value: el["@id"],
            label: el.name,
          });
        });
      }

      const values = {
        name: editData.name,
        displayOrder: editData.displayOrder,
        display: editData.display,
        blankLineAbove: editData.blankLineAbove,
        useFormula: editData.useFormula,
        formula: editData.formula || "",
        accountParameters: accountParams,
        colorText: editData.colorText,
        colorBackground: editData.colorBackground,
      };

      formik.setValues(values);
    } else {
      formik.resetForm();
    }
  }, [editData]);

  useEffect(() => {
    // Initial setup of validation schema and custom structure
    updateValidationSchema();
    updateCustomStructure();

    const optionsData = { ...options };

    if (options.accountParameters.length === 0) {
      dataService.fetchData(
        _ROUTES.ACCOUNT_PARAMETERS,
        {
          pagination: false,
          "order[name]": "ASC",
          "groups[]": "select:get",
        },
        (data) => {
          optionsData.accountParameters = buildOptions(data, "name", "@id");
          setOptions(optionsData);
        },
      );
    }
  }, []);

  // Update schema and structure when useFormula changes
  useEffect(() => {
    updateValidationSchema();
    updateCustomStructure();
  }, [formik.values.useFormula]);

  return (
    <BasicModal
      showModal={showModal}
      setShowModal={setShowModal}
      title={"Indicator"}
      content={
        <BasicFormikForm
          formik={formik}
          setShowModal={setShowModal}
          validations={validationSchema}
          customStructure={customStructure || getCustomStructure()}
          options={options}
        />
      }
      icon={<Speed />}
      width={"800"}
    />
  );
};
